import { formatRunWallClock } from '../../utils/sessionTime';

// Column definitions for the print data table
export interface PrintColumn {
  displayName: string;
  internalName: string;
  source: 'run' | 'average' | 'config';
  places?: number;
  dividerAfter?: boolean;
  invert?: boolean; // Multiply value by -1 before display
  zeroIsMissing?: boolean; // Show '-' for 0, for channels that are zero-filled when the sensor is absent
}

export interface PrintDataRow {
  type: 'config' | 'run';
  config: any;
  run?: any;
}

export function formatTimeValue(value: string | null | undefined, timezone?: string | null): string {
  if (!value) return '-';
  const formatted = formatRunWallClock(value, timezone, 'time');
  return formatted || '-';
}

export function formatCellValue(
  value: any,
  places: number | undefined,
  internalName: string,
  invert?: boolean,
  timezone?: string | null,
  zeroIsMissing?: boolean,
): string {
  if (zeroIsMissing && (Array.isArray(value) ? value[0] : value) === 0) {
    return '-';
  }

  // Special handling for run timestamp — display as wall-clock at recording location.
  if (internalName === 'run_ts') {
    return formatTimeValue(value, timezone);
  }

  // Handle string values
  if (places === -1) {
    return value != null ? String(value) : '-';
  }

  // Handle numeric values
  if (typeof value === 'number') {
    const displayValue = invert ? value * -1 : value;
    return displayValue.toFixed(places ?? 2);
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'number') {
    const displayValue = invert ? value[0] * -1 : value[0];
    return displayValue.toFixed(places ?? 2);
  }
  if (value != null) {
    return String(value);
  }
  return '-';
}

/**
 * Build rows array from configs for the print data table
 */
export function buildPrintDataRows(configs: any[]): PrintDataRow[] {
  const rows: PrintDataRow[] = [];

  for (const config of configs) {
    // Add config row
    rows.push({ type: 'config', config });
    // Add run rows
    if (config.runs) {
      for (const run of config.runs) {
        rows.push({ type: 'run', config, run });
      }
    }
  }

  return rows;
}

/**
 * Standard getCellValue function for print data tables
 */
export function getStandardCellValue(row: PrintDataRow, column: PrintColumn): any {
  if (row.type === 'config') {
    // For config rows, show config-level averages or metadata
    if (column.source === 'config') {
      return row.config[column.internalName];
    }
    if (column.source === 'average') {
      return row.config.average?.[column.internalName];
    }
    if (column.internalName === 'run_number') {
      return row.config.letter || '';
    }
    if (column.internalName === 'description') {
      return row.config.description || '';
    }
    return null;
  }

  // For run rows
  if (column.source === 'run') {
    return row.run?.[column.internalName];
  }
  if (column.source === 'average') {
    return row.run?.average?.[column.internalName];
  }
  if (column.source === 'config') {
    // Config values only shown on config rows, leave run rows blank
    return null;
  }
  return null;
}
